import { Link } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import { PageShell } from "@/components/Navbar";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Megaphone, Trash2, Plus } from "lucide-react";

const PLACEMENTS = [
  "DASHBOARD_SPOTLIGHT", "MARKETPLACE_TOP_INTERNAL", "MARKETPLACE_TOP_PUBLIC",
  "MARKETPLACE_DEVELOPER_STRIP", "CONNECTIONS_TOP", "PROPERTY_FINANCING",
];
const SPONSOR_KINDS = ["FEATURED", "SPONSORED", "PARTNER_OFFER"];
const CREATIVE_STYLES = [
  { value: "IMAGE_COPY", label: "Image + Copy" },
  { value: "FULL_CREATIVE", label: "Full Creative (artwork only)" },
];
const CAMPAIGN_TYPES = ["PROJECT", "PROFESSIONAL", "DEVELOPER", "EVENT", "FINANCING", "PARTNER", "GENERIC"];
const EMPTY = {
  placement: "DASHBOARD_SPOTLIGHT", campaign_type: "PROJECT", creative_style: "IMAGE_COPY",
  campaign_name: "", title: "", subtitle: "",
  body: "", cta_label: "Learn More", cta_destination: "/marketplace", image: "",
  sponsor_kind: "FEATURED", priority: 10, active: true,
};

export default function AdminAds() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/ad-campaigns"); setList(r.data || []); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.image.trim()) { toast.error("Creative image URL is required"); return; }
    if (form.creative_style === "IMAGE_COPY" && !form.title.trim()) { toast.error("Title is required for Image + Copy campaigns"); return; }
    setBusy(true);
    try {
      await api.post("/admin/ad-campaigns", { ...form, priority: Number(form.priority) || 0 });
      toast.success("Campaign created");
      setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const typed = window.prompt(`Permanently delete this campaign? An archived copy and its analytics are kept. To only stop it showing, use Pause or Remove in Admin → Advertising & Monetization → Sponsored Ads.\n\nType the campaign ID (${id}) to confirm:`);
    if (!typed) return;
    try { await api.delete(`/admin/ad-campaigns/${id}`, { params: { confirm: typed.trim() } }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell testid="admin-ads-page">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0F2C59] flex items-center gap-2 mb-1">
        <Megaphone className="h-7 w-7 text-[#C79A3B]" /> Spotlight & Ad Campaigns
      </h1>
      <p className="text-sm text-slate-500 mb-4" data-testid="admin-ads-manage-note">
        Create new advertisements here. Pause, resume, reschedule, remove or expire existing ones in{" "}
        <Link to="/admin?section=monetization&tab=sponsored" className="font-semibold text-[#0F2C59] underline">Admin → Advertising &amp; Monetization → Sponsored Ads</Link>.
      </p>
      <p className="text-slate-500 text-sm mb-6">Create test campaigns for each placement (Preview). Paid items are always labelled Sponsored / Partner Offer.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ad-create-form">
          <h2 className="font-semibold text-slate-900 mb-3">New Campaign</h2>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Placement</label>
              <Select value={form.placement} onValueChange={(v) => set("placement", v)}>
                <SelectTrigger data-testid="ad-form-placement"><SelectValue /></SelectTrigger>
                <SelectContent>{PLACEMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Creative Style</label>
                <Select value={form.creative_style} onValueChange={(v) => set("creative_style", v)}>
                  <SelectTrigger data-testid="ad-form-creative-style"><SelectValue /></SelectTrigger>
                  <SelectContent>{CREATIVE_STYLES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Campaign Name <span className="font-normal text-slate-400">(internal)</span></label>
                <Input data-testid="ad-form-campaign-name" value={form.campaign_name} onChange={(e) => set("campaign_name", e.target.value)} placeholder="Admin-only label" />
              </div>
            </div>
            {form.creative_style === "FULL_CREATIVE" && (
              <p className="text-[11px] text-[#92600B] bg-amber-50 rounded-lg px-2.5 py-1.5" data-testid="ad-form-full-creative-hint">
                Full Creative: the uploaded artwork is the message. Title / Subtitle / Body are not shown over it. Only the disclosure tag and (if a CTA Label is set) the CTA button appear.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Campaign Type</label>
                <Input data-testid="ad-form-type" value={form.campaign_type} onChange={(e) => set("campaign_type", e.target.value)} placeholder="PROJECT / DEVELOPER / FINANCING" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Disclosure</label>
                <Select value={form.sponsor_kind} onValueChange={(v) => set("sponsor_kind", v)}>
                  <SelectTrigger data-testid="ad-form-sponsor"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPONSOR_KINDS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-xs font-semibold text-slate-500">Title {form.creative_style === "FULL_CREATIVE" ? <span className="font-normal text-slate-400">(optional)</span> : null}</label><Input data-testid="ad-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Subtitle <span className="font-normal text-slate-400">(optional)</span></label><Input data-testid="ad-form-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></div>
            <div><label className="text-xs font-semibold text-slate-500">Body <span className="font-normal text-slate-400">(optional)</span></label><Input data-testid="ad-form-body" value={form.body} onChange={(e) => set("body", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-slate-500">CTA Label <span className="font-normal text-slate-400">(optional)</span></label><Input data-testid="ad-form-cta" value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Blank = no button" /></div>
              <div><label className="text-xs font-semibold text-slate-500">CTA Destination</label><Input data-testid="ad-form-dest" value={form.cta_destination} onChange={(e) => set("cta_destination", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-slate-500">Image URL</label><Input data-testid="ad-form-image" value={form.image} onChange={(e) => set("image", e.target.value)} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Priority</label><Input data-testid="ad-form-priority" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} /></div>
            </div>
            <Button data-testid="ad-form-submit" onClick={create} disabled={busy} className="w-full bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white rounded-xl"><Plus className="h-4 w-4 mr-1.5" /> {busy ? "Creating…" : "Create Campaign"}</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ad-list">
          <h2 className="font-semibold text-slate-900 mb-3">Campaigns ({list.length})</h2>
          <div className="space-y-2 max-h-[560px] overflow-auto">
            {list.length === 0 && <p className="text-sm text-slate-400">No campaigns yet.</p>}
            {list.map((c) => (
              <div key={c.id} data-testid={`ad-row-${c.id}`} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#0F2C59] bg-[#0F2C59]/8 rounded px-1.5 py-0.5">{c.placement}</span>
                    <span className="text-[10px] font-semibold text-[#92600B] bg-amber-50 rounded px-1.5 py-0.5">{c.sponsor_kind}</span>
                    {c.creative_style === "FULL_CREATIVE" && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">FULL CREATIVE</span>}
                    {!c.active && <span className="text-[10px] text-slate-400">inactive</span>}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 truncate">{c.title || c.campaign_name || <span className="italic text-slate-400">Untitled creative</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{c.campaign_name ? `${c.campaign_name} · ` : ""}→ {c.cta_destination || "—"}</div>
                </div>
                <button data-testid={`ad-delete-${c.id}`} onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
